import Grid from 'components/core/Grid';
import { Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import { FormikAppEmail } from 'components/formik/core/FormikAppEmail/FormikAppEmail';
import { CheckoutUserFlowDialog } from 'components/views/pages/Checkout/CheckoutUserFlow/CheckoutUserFlowDialog';
import { useToggle, useUserCheckEmailExistsMutation } from '@alluradirect/react';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  generateCheckoutUserFlowBackAction,
  generateCheckoutUserFlowDialogTitle,
} from 'components/views/pages/Checkout/CheckoutUserFlow/helpers';
import { CheckoutSectionTitle } from 'components/views/pages/Checkout/components/CheckoutSectionTitle';
import { CheckoutUserFlowEnum } from 'components/views/pages/Checkout/types';
import { CheckoutCreateAccountForm } from 'components/views/pages/Checkout/CheckoutUserFlow/CheckoutCreateAccountForm';
import { CheckoutForgotPasswordForm } from 'components/views/pages/Checkout/CheckoutUserFlow/CheckoutForgotPasswordForm';
import { CheckoutLoginForm } from 'components/views/pages/Checkout/CheckoutUserFlow/CheckoutLoginForm';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';

const initialValues = {
  email: '',
};

export type CheckoutLoginFormFormik = typeof initialValues;

const validationSchema = yup.object().shape({
  email: yup.string().email().required().min(3).max(100).label('Email'),
});

interface Props {
  isLoading: boolean;
  onClose?: () => void;
}

export const CheckoutLoginCreateAccountForm = ({ isLoading, onClose }: Props) => {
  const [checkoutUserFlowStep, setCheckoutUserFlowStep] = useState(CheckoutUserFlowEnum.Login);
  const [checkoutLoginEmail, setCheckoutLoginEmail] = useState('');

  const checkEmailExists = useUserCheckEmailExistsMutation();

  const _dialogToggle = useToggle();
  const dialogToggle = { ..._dialogToggle, close: () => { onClose?.(); _dialogToggle.close(); } };
  const onSubmit = (values: CheckoutLoginFormFormik, helpers: FormikHelpers<CheckoutLoginFormFormik>) => {
    checkEmailExists.mutate(
      {
        urlArgs: [values.email],
      },
      {
        onSuccess: (data: any) => {
          setCheckoutLoginEmail(values.email);
          if (data.DATA.IS_EMAIL_TAKEN) {
            setCheckoutUserFlowStep(CheckoutUserFlowEnum.Login);
          } else {
            setCheckoutUserFlowStep(CheckoutUserFlowEnum.CreateAccount);
          }
          dialogToggle.open();
        },
        onSettled: () => {
          helpers.setSubmitting(false);
        },
      },
    );
  };

  return (
    <>

      <Grid item xs={12}>
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          {({ isSubmitting }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LoadingSkeleton isLoading={isLoading} scaleOneToOne>
                    <CheckoutSectionTitle mb={0}>Continue to Next Step...</CheckoutSectionTitle>
                    <Typography variant="body2" mb={2}>
                      Please enter your email address.
                    </Typography>
                  </LoadingSkeleton>

                  <LoadingSkeleton isLoading={isLoading}>
                    <FormikAppEmail name="email" size="small" />
                  </LoadingSkeleton>
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    isLoading={isSubmitting}
                    isDisabled={isLoading}
                    text="Continue"
                    isFormikButton={true}
                    isFullWidth
                    size={'large'}
                    endIcon={<ChevronRight />}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>


      <CheckoutUserFlowModal
        dialogToggle={dialogToggle}
        checkoutUserFlowStep={checkoutUserFlowStep}
        setCheckoutUserFlowStep={setCheckoutUserFlowStep}
        checkoutLoginEmail={checkoutLoginEmail}
      />
    </>
  );
};

export const CheckoutUserFlowModal = ({
  dialogToggle,
  checkoutUserFlowStep,
  setCheckoutUserFlowStep,
  checkoutLoginEmail = "",
}: {
  dialogToggle: ReturnType<typeof useToggle>;
  checkoutUserFlowStep: CheckoutUserFlowEnum;
  setCheckoutUserFlowStep: Dispatch<SetStateAction<CheckoutUserFlowEnum>>;
  checkoutLoginEmail?: string;
}) => {
  const title = generateCheckoutUserFlowDialogTitle(checkoutUserFlowStep);
  const backAction = generateCheckoutUserFlowBackAction(checkoutUserFlowStep);
  return dialogToggle.isOpen ? (
    <CheckoutUserFlowDialog
      title={title}
      isOpen={dialogToggle.isOpen}
      close={dialogToggle.close}
      backAction={backAction}
      setCheckoutUserFlowStep={setCheckoutUserFlowStep}
    >
      {checkoutUserFlowStep === CheckoutUserFlowEnum.Login ? (
        <>
          <Typography variant="body1"><strong>Account Found</strong> - We have found an account using this email, please enter your password below to continue.</Typography>
          <AppDivider marginVertical='16px' />
          <AppDivider borderWidth='0px' marginVertical='8px' />
          <CheckoutLoginForm
            setCheckoutUserFlowStep={setCheckoutUserFlowStep}
            close={dialogToggle.close}
            initialEmail={checkoutLoginEmail}
          />

        </>
      ) : null
      }
      {
        checkoutUserFlowStep === CheckoutUserFlowEnum.ForgotPassword ? (
          <CheckoutForgotPasswordForm
            close={() => setCheckoutUserFlowStep(CheckoutUserFlowEnum.Login)}
            initialEmail={checkoutLoginEmail}
          />
        ) : null
      }
      {
        checkoutUserFlowStep === CheckoutUserFlowEnum.CreateAccount ? (
          <CheckoutCreateAccountForm close={dialogToggle.close} initialEmail={checkoutLoginEmail} />
        ) : null
      }
    </CheckoutUserFlowDialog >
  ) : null
}
